import type { BlobLike } from "@pdfking/compatibility"
import type { ContentType, VirtualFile } from "@pdfking/pdfengine"
import { BlobArray, BlobNative } from "@pdfking/compatibility"
import { FileContentGUID } from "@pdfking/pdfengine"
import {
  Items,
  IterableToArray,
  Keys,
  KeyTake,
  RecordCreate,
  Values,
} from "@pdfking/tfunc"
import { RequestBlob } from "@pdfking/workflow/actions/pdfengine/Functions"
import url_read_tracked from "@pdfking/workflow/utils/url_read_tracked"
import { url_query_encode } from "@pdfking/workflow/utils/urlutilities"
import get_django_url from "../../utils/get_django_url"
import get_short_name from "../naming/get_short_name"

type UploadResult = {
  remote_name: string
  type: ContentType
}

type Signing = {
  fields: Record<string, string>
  info: UploadResult
  url: string
}

type SigningResponse = {
  file: Signing[]
  success: boolean
}

async function get_content(file: VirtualFile): Promise<BlobLike> {
  return RequestBlob(file)
    .then(({ stream }) => IterableToArray(stream))
    .then(BlobArray)
}

async function make_upload_task(
  signing_request: Promise<SigningResponse>,
  name: string,
  file: VirtualFile,
  index: number
): Promise<UploadResult> {
  const response = await signing_request

  if (response.success) {
    const blob = await get_content(file)
    const signing = response.file[index]

    const data = new FormData()

    for (const [k, v] of Items(signing.fields)) {
      data.append(k, v)
    }

    data.append("file", await BlobNative(blob), get_short_name(name))

    const xhttp = await url_read_tracked(signing.url, {
      data,
    })

    if (xhttp.status < 299) return signing.info
    throw `Invalid response from server: ${xhttp.status}`
  }

  console.error(response)

  throw `Invalid response from server: ${response}`
}

export default async (files: Record<string, VirtualFile>): Promise<void> => {
  const hashes = RecordCreate(
    Items(files)
      .filter(([, f]) => !f.file_meta.remote_name)
      .map(([name, f]) => [name, FileContentGUID(f)] as const)
  )

  const total = Keys(files).length
  const to_upload = Keys(hashes).length
  console.info(`📦 ${to_upload}/${total} files to upload`)

  if (to_upload > 0) {
    const query = url_query_encode({
      name: Keys(hashes).map(s => get_short_name(s)),
      sha256: Values(hashes),
    })

    const signing_request = get_django_url("upload_s3").then(sign_url =>
      url_read_tracked(`${sign_url}?${query}`).then(r => r.json())
    )

    await Promise.all(
      Keys(hashes).map((name, index) =>
        make_upload_task(signing_request, name, files[name], index).then(
          ({ remote_name }) => {
            files[name].file_meta.remote_name = remote_name
          }
        )
      )
    )
  }
}
